import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboardWithToast } from "@/lib/clipboard";

export default function BookingConfirmation() {
  const { reference } = useParams();
  const [booking, setBooking] = useState(null);
  const [zelle, setZelle] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${reference}`).then((r) => setBooking(r.data));
    api.get("/config").then((r) => setZelle(r.data));
  }, [reference]);

  function copy(text) {
    copyToClipboardWithToast(text);
  }

  if (!booking) {
    return (
      <div className="container-x py-32 text-center text-muted-foreground" data-testid="confirmation-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="container-x py-20 md:py-28" data-testid="confirmation-page">
      <div className="max-w-2xl">
        <div className="w-12 h-12 flex items-center justify-center bg-foreground text-background mb-6">
          <Check className="w-5 h-5" />
        </div>
        <div className="eyebrow mb-3">Booking received</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.05]">Thank you, {booking.customer_name.split(" ")[0]}.</h1>
        <p className="mt-5 text-base text-foreground/70 leading-relaxed">
          Your session is reserved as <span className="text-foreground font-medium">pending</span> until we confirm your Zelle deposit. We sent confirmation details to {booking.customer_email}.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 border border-border p-7 bg-card">
          <div className="eyebrow mb-4">Booking details</div>
          <div className="space-y-3 text-sm">
            <Row label="Reference">
              <button className="inline-flex items-center gap-2 hover:underline" onClick={() => copy(booking.reference)} data-testid="copy-reference">
                <span className="font-serif text-lg">{booking.reference}</span>
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </Row>
            <Row label="Service">{booking.service_name} — {booking.tier_name}</Row>
            <Row label="Date">{booking.session_date} at {booking.session_time}</Row>
            <Row label="Total">${booking.tier_price.toFixed(2)}</Row>
            <Row label="Status">{booking.status}</Row>
          </div>
        </div>

        <div className="md:col-span-5 border border-accent p-7 bg-card" data-testid="confirmation-zelle">
          <div className="eyebrow mb-4">Send Zelle deposit</div>
          <div className="space-y-3 text-sm">
            <Row label="Send to">
              <button className="inline-flex items-center gap-2 hover:underline" onClick={() => copy(zelle?.zelle_email || "")} data-testid="copy-zelle-email">
                <span>{zelle?.zelle_email}</span>
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </Row>
            <Row label="Recipient">{zelle?.zelle_name}</Row>
            <Row label="Amount"><span className="font-serif text-xl">${booking.deposit_amount.toFixed(2)}</span></Row>
            <Row label="Memo">{booking.reference}</Row>
          </div>
          <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
            Use your Zelle-enabled bank app to send the deposit. Include the reference as the memo so we can match it to your booking.
          </p>
        </div>
      </div>

      <div className="mt-12">
        <Link to="/" className="inline-flex items-center gap-3 px-7 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90" data-testid="confirmation-home-btn">
          Back to home <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-border last:border-b-0">
      <span className="eyebrow">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}
